import { Component } from '@theme/component';

/**
 * @typedef {object} WishlistButtonRefs
 * @property {HTMLButtonElement} button - The toggle button element.
 * @property {HTMLElement} icon - The SVG icon wrapper.
 * @property {HTMLElement} liveRegion - Screen reader live region.
 */

/**
 * Wishlist toggle button that renders a heart icon with filled/outline states.
 * Communicates with `window.apa.wishlist` for backend operations.
 *
 * @extends {Component<WishlistButtonRefs>}
 */
class WishlistButton extends Component {
  requiredRefs = ['button', 'icon', 'liveRegion'];

  #inWishlist = false;
  #abortController = new AbortController();
  #isToggling = false;
  #interacted = false;

  connectedCallback() {
    super.connectedCallback();

    const productId = this.dataset.productId;
    if (!productId || !window.apa?.wishlist) return;

    // Use a fresh controller for every connection. The element may be
    // disconnected and reconnected (e.g. moved into the sticky buy bar); reusing
    // the original controller would keep its already-aborted signal, making the
    // addEventListener below a silent no-op so sync events would never arrive.
    this.#abortController = new AbortController();

    window.apa.wishlist.scheduleCheck(productId, (inWishlist) => {
      // Skip if aborted, or if the user/another button has already changed the
      // state — a slow initial check must not clobber a freshly toggled value.
      if (this.#abortController.signal.aborted || this.#interacted) return;
      this.#inWishlist = inWishlist;
      this.#updateState();
    });

    document.addEventListener('apa:wishlist:count', this.#onCountSync, {
      signal: this.#abortController.signal,
    });
    // Capture phase: product cards and quick add stop `variant:update` from bubbling
    document.addEventListener('variant:update', this.#onVariantUpdate, {
      capture: true,
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Handles the toggle action (called via on:click="/toggle").
   */
  async toggle() {
    const productId = this.dataset.productId;
    if (!productId || !window.apa?.wishlist || this.refs.button.disabled) return;

    this.refs.button.disabled = true;
    this.#interacted = true;

    // Optimistic UI
    this.#inWishlist = !this.#inWishlist;
    this.#updateState();
    this.#announce(this.#inWishlist ? this.dataset.announcementAdded : this.dataset.announcementRemoved);

    if (!this.#inWishlist) {
      this.#handleRemove(productId);
      return;
    }

    await this.#handleAdd(productId);
  }

  /**
   * Optimistic removal — dispatches events immediately and fires API in the background.
   * The wishlist section reacts to `apa:wishlist:removed` by animating the card out.
   * @param {string} productId
   */
  #handleRemove(productId) {
    window.apa.wishlist.count = Math.max(0, (window.apa.wishlist.count || 0) - 1);

    document.dispatchEvent(new CustomEvent('apa:wishlist:removed', { detail: { productId } }));
    this.#dispatchCount(productId, false);

    this.refs.button.disabled = false;

    window.apa.wishlist.toggle(productId).catch(() => {
      if (this.#abortController.signal.aborted) return;

      // Rollback the button state; section card is already gone
      this.#inWishlist = true;
      this.#updateState();
      window.apa.wishlist.count = (window.apa.wishlist.count || 0) + 1;

      this.#dispatchCount(productId, true);
    });
  }

  /**
   * Addition — awaits the API response before dispatching the count event.
   * @param {string} productId
   */
  async #handleAdd(productId) {
    try {
      const { action } = await window.apa.wishlist.toggle(productId);
      if (this.#abortController.signal.aborted) return;

      this.#inWishlist = action === 'added';
      this.#updateState();

      const delta = action === 'added' ? 1 : -1;
      window.apa.wishlist.count = (window.apa.wishlist.count || 0) + delta;

      this.#dispatchCount(productId, this.#inWishlist);
    } catch {
      if (this.#abortController.signal.aborted) return;

      this.#inWishlist = !this.#inWishlist;
      this.#updateState();
    } finally {
      if (!this.#abortController.signal.aborted) {
        this.refs.button.disabled = false;
      }
    }
  }

  /**
   * Broadcasts the new count. `variantId` and `sku` feed the Bloomreach `wishlist_update` event.
   * @param {string} productId
   * @param {boolean} inWishlist
   */
  #dispatchCount(productId, inWishlist) {
    this.#isToggling = true;
    document.dispatchEvent(
      new CustomEvent('apa:wishlist:count', {
        detail: {
          count: window.apa.wishlist.count,
          productId,
          variantId: this.dataset.variantId,
          sku: this.dataset.sku,
          inWishlist,
        },
      }),
    );
    this.#isToggling = false;
  }

  /** Syncs state when another button for the same product is toggled. */
  #onCountSync = (event) => {
    if (this.#isToggling) return;
    const { productId, inWishlist } = event.detail ?? {};
    if (productId && String(productId) === this.dataset.productId && inWishlist !== undefined) {
      this.#interacted = true;
      this.#inWishlist = inWishlist;
      this.#updateState();
    }
  };

  /**
   * Follows the selected variant so `wishlist_update` sends its id and SKU, not the initially rendered one.
   * @param {Event} event
   */
  #onVariantUpdate = (event) => {
    const { resource, data } = /** @type {CustomEvent} */ (event).detail ?? {};
    if (!resource || String(data?.productId) !== this.dataset.productId) return;
    this.dataset.variantId = String(resource.id);
    this.dataset.sku = resource.sku ?? '';
  };

  #updateState() {
    const label = this.#inWishlist ? this.dataset.labelRemove : this.dataset.labelAdd;

    this.refs.button.setAttribute('aria-pressed', String(this.#inWishlist));
    this.refs.button.setAttribute('aria-label', label);
    this.refs.icon.dataset.active = String(this.#inWishlist);
  }

  /** @param {string} [text] */
  #announce(text) {
    if (!text) return;
    this.refs.liveRegion.textContent = text;
    setTimeout(() => {
      if (!this.#abortController.signal.aborted) {
        this.refs.liveRegion.textContent = '';
      }
    }, 3000);
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}
